import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  ScrollText, Search, Filter, ChevronLeft, ChevronRight,
  Shield, UserPlus, UserMinus, Ban, Settings, Eye, AlertTriangle,
} from "lucide-react";

const ACTION_ICONS = {
  role_assigned: { icon: UserPlus, color: "text-blue-400", bg: "bg-blue-500/10" },
  moderator_assigned: { icon: Shield, color: "text-green-400", bg: "bg-green-500/10" },
  moderator_revoked: { icon: UserMinus, color: "text-red-400", bg: "bg-red-500/10" },
  user_suspended: { icon: Ban, color: "text-red-400", bg: "bg-red-500/10" },
  user_reactivated: { icon: UserPlus, color: "text-green-400", bg: "bg-green-500/10" },
  permission_changed: { icon: Shield, color: "text-purple-400", bg: "bg-purple-500/10" },
  setting_changed: { icon: Settings, color: "text-amber-400", bg: "bg-amber-500/10" },
  report_resolved: { icon: Eye, color: "text-green-400", bg: "bg-green-500/10" },
  report_dismissed: { icon: Eye, color: "text-slate-400", bg: "bg-slate-500/10" },
  case_escalated: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10" },
};

function getActionMeta(action) {
  return ACTION_ICONS[action] || { icon: ScrollText, color: "text-slate-400", bg: "bg-slate-500/10" };
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-logs`, {
        params: { page, search, action_filter: actionFilter },
        withCredentials: true,
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPages(res.data.pages);
      if (res.data.action_types) setActionTypes(res.data.action_types);
    } catch { }
    setLoading(false);
  }, [page, search, actionFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 mb-6" data-testid="audit-filters">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input type="text" placeholder="Search actor, target, action..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
              data-testid="audit-search-input" />
          </div>
          <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}
            className="px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
            data-testid="audit-action-filter">
            <option value="">All Actions</option>
            {actionTypes.map(a => (
              <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-testid="audit-table">
        {logs.length === 0 && !loading ? (
          <div className="p-12 text-center">
            <ScrollText className="mx-auto text-slate-700 mb-3" size={40} />
            <p className="text-sm text-slate-500">No audit logs yet. Actions will be recorded here.</p>
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800 text-left">
                    {["Action", "Actor", "Target", "Details", "Timestamp"].map(h => (
                      <th key={h} className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => {
                    const meta = getActionMeta(log.action);
                    return (
                      <tr key={log.id} className="border-b border-slate-800/50 hover:bg-slate-800/20" data-testid={`audit-row-${log.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className={cn("p-1.5 rounded-lg", meta.bg)}>
                              <meta.icon className={meta.color} size={14} />
                            </div>
                            <span className="text-sm text-white font-medium">{log.action.replace(/_/g, ' ')}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-300">{log.actor_name}</span>
                          <span className="text-[10px] text-slate-600 ml-1.5">{log.actor_type}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-300">{log.target_name || "—"}</span>
                          {log.target_type && <span className="text-[10px] text-slate-600 ml-1.5">{log.target_type}</span>}
                        </td>
                        <td className="px-4 py-3">
                          {log.previous_value && <span className="text-xs text-red-400/70 line-through mr-1.5">{log.previous_value}</span>}
                          {log.new_value && <span className="text-xs text-green-400">{log.new_value}</span>}
                          {log.reason && <div className="text-[10px] text-slate-500 mt-0.5 italic">"{log.reason}"</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</span>
                          {log.ip_address && <div className="text-[10px] text-slate-600">{log.ip_address}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="lg:hidden divide-y divide-slate-800/50">
              {logs.map(log => {
                const meta = getActionMeta(log.action);
                return (
                  <div key={log.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={cn("p-1.5 rounded-lg", meta.bg)}>
                          <meta.icon className={meta.color} size={14} />
                        </div>
                        <span className="text-sm text-white font-medium">{log.action.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="text-[10px] text-slate-600">{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-slate-400">
                      <span className="text-slate-300">{log.actor_name}</span> on <span className="text-slate-300">{log.target_name || "—"}</span>
                    </div>
                    {log.reason && <div className="text-[10px] text-slate-500 italic">"{log.reason}"</div>}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
                <span className="text-xs text-slate-500">Page {page} of {pages} ({total} entries)</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="p-1.5 rounded bg-slate-800 text-slate-400 disabled:opacity-30"><ChevronLeft size={16} /></button>
                  <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                    className="p-1.5 rounded bg-slate-800 text-slate-400 disabled:opacity-30"><ChevronRight size={16} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
